import { FC, Fragment, useCallback, useRef, useState, SyntheticEvent } from "react";
import { Box, Button, Card, CardContent, CardHeader, FormControlLabel,
  Grid, Radio, RadioGroup, Drawer, Typography, useMediaQuery } from "@mui/material";
import { useRecordContext, crudGetAll } from "ra-core";
import { useDispatch } from "react-redux";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { RecPatch } from "@/dataProvider/tools";
import { sorts } from "@/utils/paramsWithGetMainList"
import { merge } from "lodash"
import { useSnackbar } from "notistack";
import { ProductReleaseDto, ProductType } from "@/gen/api/user"
import ReadonlyField from "@/components/input/ReadonlyField";


const getTypeText = (type: ProductType | undefined) => {
  switch(type) {
    case ProductType.EncryptionTool: 
      return 'Encryption Tool '
    case ProductType.SecureFile:
      return 'SecureFile '
    case ProductType.OnDataOdbc:
      return 'OnData ODBC Driver '
    case ProductType.OnDataJdbc:
      return 'OnData JDBC Driver '
    case ProductType.OnDataSqlClient:
      return 'OnData SqlClient Driver '
    default:
      return 'Unknow '
  }
}

const ReleaseDownloadView: FC<{
  open: boolean;
  setOpen: (x: boolean) => void;
  productType?: ProductType;
  releaseId?: number;
}> = ({ open, setOpen, productType, releaseId }) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  const record = useRecordContext<ProductReleaseDto&RecPatch>()
  const cardTitle = getTypeText(record?.productType) + 'Download'
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const getInitialPlatformType = (rec: ProductReleaseDto&RecPatch) => {
    return rec.windowsAvailable ? 0 : rec.linuxAvailable ? 1 : rec.macAvailable ? 2 : rec.macLegacyAvailable ? 3 : 0
  }
  const [platformType, setPlatformType] = useState(getInitialPlatformType(record))

  const recordRef = useRef<boolean | null>(!!record)
  
  const handleClose = useCallback(() => { 
    setOpen(false) 
    recordRef.current = null
  }, [setOpen])

  const handleDownload = useCallback((releaseId: number, platform: number) => (e: SyntheticEvent) => {
    dispatch(merge(
      crudGetAll('download-product-release', sorts(), { releaseId, platform }, 0, (res)=> {
        if(res.payload.data[0].downloadUri != ''){
          const url = new URL(res.payload.data[0].downloadUri).toString()
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', res.payload.data[0].fileName)
          document.body.appendChild(link)
          link.click()
        }else{
          enqueueSnackbar(res.payload.data[0].errMsg || 'failed to download', { variant: 'error' })
        }
      })
    ))
  }, [dispatch])

  const format = useUserTimeFormat()
  
  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <Card sx={{ overflowY: 'auto', mb: 4, }}>
        <CardHeader title={cardTitle} />
        <CardContent>
          <Grid
            container
            spacing={4}
          >
            <Grid item xs={12} >
              <ReadonlyField disabled={true} label="Release Time" value={format(record?.releaseTime || 0, 'PPp')} />
            </Grid>
            <Grid item xs={12} >
              <ReadonlyField disabled={true} label="Version" value={record?.productVersion} />
            </Grid>
            <Grid item xs={12} >
              <ReadonlyField disabled={true} label="Tag" value={record?.releaseTag} />
            </Grid>
            <Grid item xs={12} >
              <Box>
                <Typography variant="subtitle2">Platform</Typography>
                <RadioGroup name="platformType" value={platformType} onChange={(e)=> setPlatformType(Number.parseInt(e.target.value))} row>
                  <FormControlLabel value={0} control={<Radio />} label="Windows-x64" disabled={!record?.windowsAvailable}/>
                  <FormControlLabel value={1} control={<Radio />} label="Linux-x64" disabled={!record?.linuxAvailable}/>
                  <FormControlLabel value={2} control={<Radio />} label="MacOS-arm64" disabled={!record?.macAvailable}/>
                  <FormControlLabel value={3} control={<Radio />} label="MacOS-x64" disabled={!record?.macLegacyAvailable}/>
                </RadioGroup>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2
          }}
        >
          <Button
            color="primary"
            variant="outlined"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleDownload(record.releaseId || 0, platformType)}
            variant="contained"
            disabled={!record?.windowsAvailable && !record?.linuxAvailable && !record?.macAvailable && !record?.macLegacyAvailable}
          >
            Download
          </Button>
        </Box>
      </Card>
    </Drawer>
  )
}

export default ReleaseDownloadView;

